import React from "react";
import {
  Box,
  Typography,
  FormControl,
  OutlinedInput,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SearchResult from "../../components/SearchResult";
import ApiService from "../../services/api-service";
import Loader from "./../../components/Loader";
import PageHeader from "../../components/PageHeader";
import AddMusicToPlaylist from "../../components/AddMusicToPlaylist";
import { useTranslation } from "react-i18next";
import './index.css'; // Ensure this path is correct
import { checkElasticSearchHealth } from '../../utils/checkElasticSearch';

export default function Search({ showClose }) {
  const { t } = useTranslation("common");
  const [showPlaylist, setShowPlaylist] = React.useState(false);
  const [selectedIndex, setSelectedIndex] = React.useState(null);
  const [selectedId, setSelectedAlbum] = React.useState();
  const [focus, setFocus] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [user, setUser] = React.useState([]);
  const [playlist, setPlaylist] = React.useState([]);
  const [music, setMusic] = React.useState([]);
  const [keyword, setKeyword] = React.useState("");
  const [isTyping, setIsTyping] = React.useState(false);
  const typingTimerRef = React.useRef(null);
  const doneTypingInterval = 1000;

  // Cleanup timer on unmount
  React.useEffect(() => {
    return () => {
      if (typingTimerRef.current) {
        clearTimeout(typingTimerRef.current);
      }
    };
  }, []);

  const handleSearch = async (keyword) => {
    console.log("[Search] handleSearch called with keyword:", keyword);
    if (keyword) {
      try {
        setLoading(true);
        const response = await ApiService.SEARCH(keyword);
        console.log("[Search] API Response:", response);
        setMusic([...response["result"]["music"]]);
        setPlaylist([...response["result"]["studio"]]);
        setUser([...response["result"]["artist"]]);
        setLoading(false);
      } catch (error) {
        console.error("[Search] Error:", error);
        setLoading(false);
        
        // Check if it's an Elasticsearch connectivity issue
        if (error.response && (error.response.status === 503 || error.response.status === 500)) {
          console.error("[Search] Elasticsearch may be down. Running diagnostic...");
          checkElasticSearchHealth();
          
          // Show user-friendly error
          setMusic([]);
          setPlaylist([]);
          setUser([]);
          // You might want to show a notification here
          // notifier.error("Search is temporarily unavailable. Please try again later.");
        }
      }
    }
  };

  // Use useCallback to prevent recreating the function on every render
  const doneTyping = React.useCallback(() => {
    console.log("[Search] doneTyping called, keyword:", keyword);
    setFocus(true);
    if (keyword.length >= 1) {
      handleSearch(keyword);
    }
  }, [keyword]);

  const onKeyup = React.useCallback(() => {
    clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(doneTyping, doneTypingInterval);
    setIsTyping(false);
  }, [doneTyping]);

  const onKeydown = React.useCallback(() => {
    clearTimeout(typingTimerRef.current);
    setIsTyping(true);
  }, []);

  React.useEffect(() => {
    // Prevent body scroll when search results are open
    if (focus && (user.length > 0 || music.length > 0 || playlist.length > 0)) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [focus, user.length, music.length, playlist.length]);

  return (
    <>
      {/* Search Results - rendered normally without portal */}
      {focus && !loading && (user.length > 0 || music.length > 0 || playlist.length > 0) && (
        <>
          {/* Backdrop */}
          <Box
            sx={{
              position: 'fixed',
              top: '80px',
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              backdropFilter: 'blur(4px)',
              zIndex: 998,
            }}
            onClick={() => {
              setKeyword("");
              setFocus(false);
            }}
          />
          {/* Search Results */}
          <Box sx={{ position: 'relative', zIndex: 999 }}>
            <SearchResult
              handleClose={() => {
                setKeyword("");
                setFocus(false);
              }}
              keyword={keyword}
              users={user}
              musics={music}
              playlists={playlist}
              onShowAddPlaylist={(musicId, show) => {
                setSelectedAlbum(musicId);
                setShowPlaylist(show);
              }}
              style={{ maxHeight: '80vh', overflowY: 'auto' }}
            />
          </Box>
        </>
      )}
      
      <Box
        sx={{
          display: "flex",
          justifyContent: 'center',
          width: showClose ? "100%" : "100%",
          minWidth: "300px",
          maxWidth: "700px",
          position: "relative",
          height: "auto",
          paddingLeft: "18px",
          paddingRight: "18px",
          margin: "0 auto",
        }}
      >

      <div className={`search-input-container`}>
        <OutlinedInput
          sx={{
            width: "100%",
            borderRadius: "20px",
            height: "36px",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#6a6a6a",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#6a6a6a",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#6a6a6a",
              borderWidth: "1px",
            },
            "&.Mui-focused": {
              boxShadow: "none",
            },
          }}
          id="search-input"
          className={`search-input ${focus ? "focused" : ""}`}
          placeholder={t("header.search")}
          value={keyword}
          onChange={(e) => {
            console.log("[Search] Input changed:", e.target.value);
            setKeyword(e.target.value);
            setFocus(true);
          }}
          onKeyUp={onKeyup}
          onKeyDown={onKeydown}
          startAdornment={
            <InputAdornment position="start" id="_search_icon">
              {showClose ?? <SearchIcon />}
            </InputAdornment>
          }
          aria-describedby="outlined-weight-helper-text"
          inputProps={{
            "aria-label": "Search Artist, Song, Studio Name",
          }}
          autoComplete="off"
        />
      </div>

        {/* Removed loader to prevent yellow line when typing */}
        {showPlaylist ? (
          <AddMusicToPlaylist
            musicId={selectedId}
            open={showPlaylist}
            handleClose={() => {
              setShowPlaylist(false);
            }}
          />
        ) : null}
      </Box>
    </>
  );
}